import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fmtAED } from '../utils/money';
import { 
  ArrowRight, Receipt, Plus, Search, Filter, Download, Eye, 
  Edit3, Trash2, Loader2, CheckCircle, XCircle, Clock, 
  User, Phone, Mail, Calendar, DollarSign, FileText,
  CreditCard, Banknote, Building2, ChevronDown, Home, Settings, LogOut, ChevronLeft
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { DirhamPrice } from '../components/DirhamPrice';

// Logo
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

// CloudGM Logo for PDF
// CloudGM FZ LLC official logo — used in the invoice header (consistent with Dashboard).
const CLOUDGM_LOGO = '/brand/ast-logo-cloudgm.png';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceManagement() {
  const { platform } = useParams(); // cloudgm or cloudgm-business
  const [searchParams] = useSearchParams();
  const fromFinance = searchParams.get('from') === 'finance';
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { language } = useLanguage();
  
  // User Dropdown removed — header simplified (Round 40 cleanup)
  
  const [activeView, setActiveView] = useState('list'); // list, create, detail
  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [dropdowns, setDropdowns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Form state
  const [isRegisteredCustomer, setIsRegisteredCustomer] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerData, setCustomerData] = useState(null);
  // Autocomplete state for the registered-customer search field
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    service_type: '',
    description: '',
    subtotal: 0,
    tax_percentage: 5,
    tax_amount: 0,
    total_amount: 0,
    payment_method: '',
    payment_status: 'unpaid',
    notes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  const platformKey = platform === 'cloudgm-business' ? 'cloudgm_business' : 'cloudgm';
  const isCustomerPlatform = platform === 'cloudgm';

  useEffect(() => {
    fetchInvoices();
    fetchDropdowns();
  }, [platform]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/invoices/list/${platformKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setInvoices(response.data.invoices);
        setStatistics(response.data.statistics);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error(language === 'ar' ? 'خطأ في جلب الفواتير' : 'Error fetching invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const response = await axios.get(
        `${API}/api/invoices/dropdowns/${platformKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setDropdowns(response.data.dropdowns);
      }
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  const lookupCustomer = async () => {
    if (!customerEmail) return;
    
    try {
      setLookingUpCustomer(true);
      const response = await axios.get(
        `${API}/api/invoices/customer-lookup/${platformKey}/${customerEmail}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.customer) {
        setCustomerData(response.data.customer);
        setFormData(prev => ({
          ...prev,
          customer_name: response.data.customer.full_name || '',
          customer_phone: response.data.customer.phone || '',
          customer_email: response.data.customer.email || ''
        }));
        toast.success(language === 'ar' ? 'تم العثور على العميل' : 'Customer found');
      } else {
        setCustomerData(null);
        toast.error(language === 'ar' ? 'العميل غير موجود' : 'Customer not found');
      }
    } catch (error) {
      console.error('Error looking up customer:', error);
      toast.error(language === 'ar' ? 'خطأ في البحث عن العميل' : 'Error looking up customer');
    } finally {
      setLookingUpCustomer(false);
    }
  };

  // Debounced autocomplete: search registered customers as user types (>=2 chars).
  useEffect(() => {
    if (!isRegisteredCustomer) {
      setCustomerSuggestions([]);
      return undefined;
    }
    const q = customerEmail.trim();
    if (q.length < 2) {
      setCustomerSuggestions([]);
      return undefined;
    }
    // If a customer is already selected and the email matches, no need to search.
    if (customerData && customerData.email === q) {
      setCustomerSuggestions([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        setSearchingCustomers(true);
        const response = await axios.get(
          `${API}/api/invoices/customer-search/${platformKey}`,
          { params: { q, limit: 8 }, headers: { Authorization: `Bearer ${token}` } },
        );
        setCustomerSuggestions(response.data?.data || []);
        setShowSuggestions(true);
      } catch (err) {
        // Soft-fail — autocomplete should never block typing
      } finally {
        setSearchingCustomers(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [customerEmail, isRegisteredCustomer, customerData, platformKey, token]);

  const pickCustomerSuggestion = (sug) => {
    setCustomerData(sug);
    setCustomerEmail(sug.email);
    setFormData((prev) => ({
      ...prev,
      customer_name: sug.full_name || '',
      customer_phone: sug.phone || '',
      customer_email: sug.email || '',
    }));
    setShowSuggestions(false);
    setCustomerSuggestions([]);
  };

  const calculateTotals = (subtotal, taxPercentage) => {
    const taxAmount = (subtotal * taxPercentage) / 100;
    const totalAmount = subtotal + taxAmount;
    return { taxAmount, totalAmount };
  };

  const handleSubtotalChange = (value) => {
    const subtotal = parseFloat(value) || 0;
    const { taxAmount, totalAmount } = calculateTotals(subtotal, formData.tax_percentage);
    setFormData(prev => ({
      ...prev,
      subtotal,
      tax_amount: taxAmount,
      total_amount: totalAmount
    }));
  };

  const handleTaxChange = (value) => {
    const taxPercentage = parseFloat(value) || 0;
    const { taxAmount, totalAmount } = calculateTotals(formData.subtotal, taxPercentage);
    setFormData(prev => ({
      ...prev,
      tax_percentage: taxPercentage,
      tax_amount: taxAmount,
      total_amount: totalAmount
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.service_type || !formData.payment_method) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    // iter-237.5 (Feb 2026) — Email is now MANDATORY (persistent link key)
    // and must be well-formed. Phone stays optional.
    const email = (formData.customer_email || '').trim();
    if (!email) {
      toast.error(language === 'ar' ? 'البريد الإلكتروني مطلوب — هو مفتاح ربط الفواتير بحساب العميل' : 'Email is required — used to link invoices to the customer account');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(language === 'ar' ? 'صيغة البريد الإلكتروني غير صحيحة' : 'Invalid email format');
      return;
    }
    const phone = (formData.customer_phone || '').trim();
    if (phone && !/^\+?[0-9\s\-()]{7,20}$/.test(phone)) {
      toast.error(language === 'ar' ? 'صيغة رقم الهاتف غير صحيحة' : 'Invalid phone format');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const invoiceData = {
        platform: platformKey,
        is_registered_customer: isRegisteredCustomer,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        service_type: formData.service_type,
        description: formData.description,
        items: [],
        subtotal: formData.subtotal,
        tax_percentage: formData.tax_percentage,
        tax_amount: formData.tax_amount,
        total_amount: formData.total_amount,
        payment_method: formData.payment_method,
        payment_status: formData.payment_status,
        notes: formData.notes
      };
      
      const response = await axios.post(
        `${API}/api/invoices/create`,
        invoiceData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(language === 'ar' ? `تم إنشاء الفاتورة رقم ${response.data.invoice.invoice_number}` : `Invoice ${response.data.invoice.invoice_number} created`);
        resetForm();
        setActiveView('list');
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(language === 'ar' ? 'خطأ في إنشاء الفاتورة' : 'Error creating invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      service_type: '',
      description: '',
      subtotal: 0,
      tax_percentage: 5,
      tax_amount: 0,
      total_amount: 0,
      payment_method: '',
      payment_status: 'unpaid',
      notes: ''
    });
    setIsRegisteredCustomer(false);
    setCustomerEmail('');
    setCustomerData(null);
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    try {
      const response = await axios.delete(
        `${API}/api/invoices/delete/${platformKey}/${invoiceToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(language === 'ar' ? 'تم حذف الفاتورة' : 'Invoice deleted');
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error(language === 'ar' ? 'خطأ في حذف الفاتورة' : 'Error deleting invoice');
    } finally {
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const updatePaymentStatus = async (invoiceId, newStatus) => {
    try {
      const response = await axios.put(
        `${API}/api/invoices/update/${platformKey}/${invoiceId}`,
        { payment_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(language === 'ar' ? 'تم تحديث حالة الدفع' : 'Payment status updated');
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error(language === 'ar' ? 'خطأ في تحديث الفاتورة' : 'Error updating invoice');
    }
  };

  // Download Invoice as PDF
  const downloadInvoicePDF = async (invoice) => {
    try {
      toast.loading(language === 'ar' ? 'جاري إنشاء الفاتورة...' : 'Generating invoice...');
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Company name based on platform
      const companyName = platform === 'cloudgm' ? 'CLOUDGM' : 'CLOUDGM Business';
      
      // Load logo image using fetch with CORS proxy
      const loadImageAsBase64 = async (url) => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.log('Fetch failed, trying Image method');
          // Fallback to Image method
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
            img.src = url;
          });
        }
      };

      // Try to load logo
      let logoLoaded = false;
      try {
        const logoData = await loadImageAsBase64(CLOUDGM_LOGO);
        if (logoData) {
          doc.addImage(logoData, 'PNG', 15, 10, 35, 35);
          logoLoaded = true;
        }
      } catch (e) {
        console.log('Could not load logo:', e);
      }

      // Company Name Header - adjust position based on logo
      const headerY = logoLoaded ? 30 : 20;
      doc.setFontSize(24);
      doc.setTextColor(40, 40, 40);
      doc.text(companyName, pageWidth - 15, headerY, { align: 'right' });
      
      // Invoice Title
      doc.setFontSize(16);
      doc.setTextColor(100, 100, 100);
      doc.text('INVOICE', pageWidth - 15, headerY + 10, { align: 'right' });
      
      // Divider line
      const dividerY = logoLoaded ? 55 : 45;
      doc.setDrawColor(212, 175, 55);
      doc.setLineWidth(0.5);
      doc.line(15, dividerY, pageWidth - 15, dividerY);
      
      // Invoice Details
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      
      let y = logoLoaded ? 70 : 60;
      
      // Invoice Number and Date
      doc.setFont(undefined, 'bold');
      doc.text('Invoice Number:', 15, y);
      doc.setFont(undefined, 'normal');
      doc.text(invoice.invoice_number || 'N/A', 60, y);
      
      doc.setFont(undefined, 'bold');
      doc.text('Date:', pageWidth - 80, y);
      doc.setFont(undefined, 'normal');
      doc.text(new Date(invoice.created_at).toLocaleDateString('en-GB'), pageWidth - 55, y);
      
      y += 10;
      
      // Payment Status
      doc.setFont(undefined, 'bold');
      doc.text('Status:', 15, y);
      doc.setFont(undefined, 'normal');
      const statusText = invoice.payment_status === 'paid' ? 'PAID' : invoice.payment_status === 'partial' ? 'PARTIAL' : 'UNPAID';
      doc.setTextColor(invoice.payment_status === 'paid' ? 34 : 220, invoice.payment_status === 'paid' ? 197 : 53, invoice.payment_status === 'paid' ? 94 : 69);
      doc.text(statusText, 60, y);
      doc.setTextColor(60, 60, 60);
      
      y += 20;
      
      // Customer Information
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Bill To:', 15, y);
      y += 8;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(invoice.customer_name || 'N/A', 15, y);
      y += 6;
      if (invoice.customer_email) {
        doc.text(invoice.customer_email, 15, y);
        y += 6;
      }
      if (invoice.customer_phone) {
        doc.text(invoice.customer_phone, 15, y);
        y += 6;
      }
      
      y += 10;
      
      // Service Details Table
      doc.setFillColor(245, 245, 245);
      doc.rect(15, y, pageWidth - 30, 10, 'F');
      doc.setFont(undefined, 'bold');
      doc.text('Description', 20, y + 7);
      doc.text('Amount', pageWidth - 40, y + 7, { align: 'right' });
      
      y += 15;
      doc.setFont(undefined, 'normal');
      
      // Service Type
      const serviceLabel = getDropdownLabel('service_types', invoice.service_type) || invoice.service_type || 'Service';
      doc.text(serviceLabel, 20, y);
      doc.text(`AED ${fmtAED(invoice.subtotal)}`, pageWidth - 40, y, { align: 'right' });
      
      if (invoice.description) {
        y += 6;
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(invoice.description.substring(0, 80), 20, y);
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(10);
      }
      
      y += 20;
      
      // Totals
      doc.setDrawColor(200, 200, 200);
      doc.line(pageWidth - 100, y, pageWidth - 15, y);
      y += 8;
      
      doc.text('Subtotal:', pageWidth - 80, y);
      doc.text(`AED ${fmtAED(invoice.subtotal)}`, pageWidth - 20, y, { align: 'right' });
      y += 8;
      
      doc.text(`VAT (${invoice.tax_percentage || 5}%):`, pageWidth - 80, y);
      doc.text(`AED ${fmtAED(invoice.tax_amount)}`, pageWidth - 20, y, { align: 'right' });
      y += 8;
      
      doc.setDrawColor(200, 200, 200);
      doc.line(pageWidth - 100, y, pageWidth - 15, y);
      y += 8;
      
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.text('Total:', pageWidth - 80, y);
      doc.setTextColor(212, 175, 55);
      doc.text(`AED ${fmtAED(invoice.total_amount)}`, pageWidth - 20, y, { align: 'right' });
      
      // Notes
      if (invoice.notes) {
        y += 25;
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Notes:', 15, y);
        y += 6;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(invoice.notes.substring(0, 100), 15, y);
      }
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Thank you for your business!', pageWidth / 2, 280, { align: 'center' });
      doc.text(companyName + ' | UAE', pageWidth / 2, 285, { align: 'center' });
      
      // Save PDF
      doc.save(`Invoice_${invoice.invoice_number || 'unknown'}.pdf`);
      
      toast.dismiss();
      toast.success(language === 'ar' ? 'تم تحميل الفاتورة' : 'Invoice downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.dismiss();
      toast.error(language === 'ar' ? 'خطأ في إنشاء الفاتورة' : 'Error generating invoice');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { label: language === 'ar' ? 'مدفوع' : 'Paid', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
      unpaid: { label: language === 'ar' ? 'غير مدفوع' : 'Unpaid', color: 'bg-red-500/20 text-red-400', icon: XCircle },
      partial: { label: language === 'ar' ? 'جزئي' : 'Partial', color: 'bg-amber-500/20 text-amber-400', icon: Clock }
    };
    const config = statusConfig[status] || statusConfig.unpaid;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getDropdownLabel = (category, id) => {
    if (!dropdowns || !dropdowns[category]) return id;
    const item = dropdowns[category].find(i => i.id === id);
    return item ? (language === 'ar' ? item.name_ar : item.name_en) : id;
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = !searchQuery || 
      inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.customer_email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inv.payment_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getBreadcrumb = () => {
    const base = isCustomerPlatform 
      ? { ar: 'لوحة التحكم / CLOUDGM FZ LLC / منصة العملاء / الإشراف على المنصة / الفواتير', en: 'Dashboard / CLOUDGM FZ LLC / Customers Platform / Platform Supervision / Invoices' }
      : { ar: 'لوحة التحكم / CLOUDGM FZ LLC / منصة التجار / الإشراف على المنصة / الفواتير', en: 'Dashboard / CLOUDGM FZ LLC / Merchants Platform / Platform Supervision / Invoices' };
    
    if (activeView === 'create') {
      return {
        ar: base.ar + ' / إنشاء فاتورة',
        en: base.en + ' / Create Invoice'
      };
    }
    return base;
  };

  const breadcrumbPaths = getBreadcrumb();

  // ==================== Render Invoice List ====================
  const renderInvoiceList = () => (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#1a1a2e] rounded-xl p-4 border border-[#2a2a4e]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-[#808080]">{language === 'ar' ? 'إجمالي الفواتير' : 'Total Invoices'}</p>
                <p className="text-lg font-bold text-white">{statistics.total_invoices}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1a1a2e] rounded-xl p-4 border border-[#2a2a4e]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#c9a227]/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#c9a227]" />
              </div>
              <div>
                <p className="text-xs text-[#808080]">{language === 'ar' ? 'إجمالي المبلغ' : 'Total Amount'}</p>
                <p className="text-lg font-bold text-white"><DirhamPrice value={statistics.total_amount} decimals={0} size="sm" /></p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1a1a2e] rounded-xl p-4 border border-[#2a2a4e]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-[#808080]">{language === 'ar' ? 'المدفوع' : 'Paid'}</p>
                <p className="text-lg font-bold text-emerald-400"><DirhamPrice value={statistics.paid_amount} decimals={0} size="sm" /></p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1a1a2e] rounded-xl p-4 border border-[#2a2a4e]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-[#808080]">{language === 'ar' ? 'غير المدفوع' : 'Unpaid'}</p>
                <p className="text-lg font-bold text-red-400"><DirhamPrice value={statistics.unpaid_amount} decimals={0} size="sm" /></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#606060]" />
          <Input
            placeholder={language === 'ar' ? 'بحث بالرقم أو اسم العميل...' : 'Search by number or customer...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10 bg-[#1a1a2e] border-[#2a2a4e] text-white"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#1a1a2e] border border-[#2a2a4e] rounded-lg px-4 py-2 text-white text-sm"
        >
          <option value="all">{language === 'ar' ? 'جميع الحالات' : 'All Status'}</option>
          <option value="paid">{language === 'ar' ? 'مدفوع' : 'Paid'}</option>
          <option value="unpaid">{language === 'ar' ? 'غير مدفوع' : 'Unpaid'}</option>
          <option value="partial">{language === 'ar' ? 'جزئي' : 'Partial'}</option>
        </select>
        
        <Button
          onClick={() => setActiveView('create')}
          className="bg-[#c9a227] hover:bg-[#b8922a] text-black"
        >
          <Plus className="w-4 h-4 ml-2" />
          {language === 'ar' ? 'إنشاء فاتورة' : 'Create Invoice'}
        </Button>
      </div>

      {/* Invoices Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#c9a227] animate-spin" />
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="text-center py-12">
          <Receipt className="w-12 h-12 text-[#404040] mx-auto mb-4" />
          <p className="text-[#808080]">{language === 'ar' ? 'لا توجد فواتير' : 'No invoices found'}</p>
        </div>
      ) : (
        <div className="bg-[#1a1a2e] rounded-xl border border-[#2a2a4e] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0f0f1a]">
                <tr>
                  <th className="text-right px-4 py-3 text-xs text-[#808080] font-medium">{language === 'ar' ? 'رقم الفاتورة' : 'Invoice #'}</th>
                  <th className="text-right px-4 py-3 text-xs text-[#808080] font-medium">{language === 'ar' ? 'العميل' : 'Customer'}</th>
                  <th className="text-right px-4 py-3 text-xs text-[#808080] font-medium">{language === 'ar' ? 'الخدمة' : 'Service'}</th>
                  <th className="text-right px-4 py-3 text-xs text-[#808080] font-medium">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                  <th className="text-right px-4 py-3 text-xs text-[#808080] font-medium">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                  <th className="text-right px-4 py-3 text-xs text-[#808080] font-medium">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                  <th className="text-center px-4 py-3 text-xs text-[#808080] font-medium">{language === 'ar' ? 'إجراءات' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2a4e]">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-[#252540] transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-[#c9a227]">{invoice.invoice_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-white">{invoice.customer_name}</p>
                        <p className="text-xs text-[#606060]">{invoice.customer_phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white">{getDropdownLabel('service_types', invoice.service_type)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-white"><DirhamPrice value={invoice.total_amount} decimals={0} size="sm" /></span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(invoice.payment_status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-[#808080]">
                        {new Date(invoice.created_at).toLocaleDateString('ar-AE')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => { setSelectedInvoice(invoice); setActiveView('detail'); }}
                          className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                          title={language === 'ar' ? 'عرض' : 'View'}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => downloadInvoicePDF(invoice)}
                          className="p-1.5 rounded-lg bg-[#d4af37]/20 text-[#d4af37] hover:bg-[#d4af37]/30 transition-colors"
                          title={language === 'ar' ? 'تحميل PDF' : 'Download PDF'}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {invoice.payment_status !== 'paid' && (
                          <button
                            onClick={() => updatePaymentStatus(invoice.id, 'paid')}
                            className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                            title={language === 'ar' ? 'تحديد كمدفوع' : 'Mark as Paid'}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => { setInvoiceToDelete(invoice); setDeleteDialogOpen(true); }}
                          className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                          title={language === 'ar' ? 'حذف' : 'Delete'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // ==================== Render Create Invoice Form ====================
  const renderCreateForm = () => (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-6">
      {/* Customer Type Toggle */}
      <div className="bg-[#1a1a2e] rounded-xl p-6 border border-[#2a2a4e]">
        <h3 className="text-lg font-bold text-white mb-4">{language === 'ar' ? 'معلومات العميل' : 'Customer Information'}</h3>
        
        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isRegisteredCustomer}
              onChange={(e) => {
                setIsRegisteredCustomer(e.target.checked);
                if (!e.target.checked) {
                  setCustomerData(null);
                  setCustomerEmail('');
                }
              }}
              className="w-4 h-4 rounded border-[#2a2a4e] bg-[#0a0a0a] text-[#c9a227] focus:ring-[#c9a227]"
            />
            <span className="text-sm text-white">{language === 'ar' ? 'العميل مسجل في المنصة' : 'Registered Customer'}</span>
          </label>
        </div>
        
        {isRegisteredCustomer ? (
          <div className="space-y-4">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder={language === 'ar' ? 'ابدأ بكتابة البريد، الاسم أو الهاتف...' : 'Type email, name or phone...'}
                  value={customerEmail}
                  onChange={(e) => {
                    setCustomerEmail(e.target.value);
                    if (customerData) setCustomerData(null);
                  }}
                  onFocus={() => customerSuggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="w-full bg-[#0a0a0a] border-[#2a2a4e] text-white"
                  data-testid="customer-search-input"
                  autoComplete="off"
                />
                {searchingCustomers && (
                  <Loader2 className="w-4 h-4 absolute end-3 top-1/2 -translate-y-1/2 animate-spin text-[#71717A]" />
                )}
                {showSuggestions && customerSuggestions.length > 0 && (
                  <div
                    className="absolute z-30 left-0 right-0 top-full mt-1 bg-[#1a1a2e] border border-[#2a2a4e] rounded-xl shadow-2xl max-h-72 overflow-y-auto"
                    data-testid="customer-suggestions-list"
                  >
                    {customerSuggestions.map((s) => (
                      <button
                        key={s.id || s.email}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickCustomerSuggestion(s); }}
                        className="w-full px-4 py-3 flex items-start gap-3 text-start hover:bg-[#d4af37]/10 border-b border-white/5 last:border-0 transition-colors"
                        data-testid={`customer-suggestion-${s.id || s.email}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center text-[#d4af37] font-bold text-sm flex-shrink-0">
                          {(s.full_name || s.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{s.full_name || (language === 'ar' ? 'بدون اسم' : 'No name')}</p>
                          <p className="text-[#A1A1AA] text-xs truncate" dir="ltr">{s.email}</p>
                          {s.phone && (
                            <p className="text-[#71717A] text-[11px] truncate" dir="ltr">{s.phone}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {showSuggestions && !searchingCustomers && customerEmail.trim().length >= 2 && customerSuggestions.length === 0 && (
                  <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-[#1a1a2e] border border-[#2a2a4e] rounded-xl shadow-2xl p-4 text-center text-[#71717A] text-sm">
                    {language === 'ar' ? 'لا توجد نتائج' : 'No matches'}
                  </div>
                )}
              </div>
              <Button
                type="button"
                onClick={lookupCustomer}
                disabled={lookingUpCustomer || !customerEmail}
                className="bg-[#2a2a4e] hover:bg-[#3a3a5e] text-white"
                data-testid="customer-lookup-btn"
                title={language === 'ar' ? 'بحث مطابق تماماً' : 'Exact match search'}
              >
                {lookingUpCustomer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            
            {customerData && (
              <div className="bg-[#0a0a0a] rounded-lg p-4 border border-emerald-500/30">
                <div className="flex items-center gap-2 text-emerald-400 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{language === 'ar' ? 'تم العثور على العميل' : 'Customer Found'}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#606060]">{language === 'ar' ? 'الاسم' : 'Name'}</p>
                    <p className="text-white">{customerData.full_name}</p>
                  </div>
                  <div>
                    <p className="text-[#606060]">{language === 'ar' ? 'الهاتف' : 'Phone'}</p>
                    <p className="text-white">{customerData.phone || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'اسم العميل *' : 'Customer Name *'}</label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}</label>
              <Input
                value={formData.customer_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
          </div>
        )}
      </div>

      {/* Service Details */}
      <div className="bg-[#1a1a2e] rounded-xl p-6 border border-[#2a2a4e]">
        <h3 className="text-lg font-bold text-white mb-4">{language === 'ar' ? 'تفاصيل الخدمة' : 'Service Details'}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'نوع الخدمة *' : 'Service Type *'}</label>
            <select
              value={formData.service_type}
              onChange={(e) => setFormData(prev => ({ ...prev, service_type: e.target.value }))}
              className="w-full bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 text-white text-sm"
              required
            >
              <option value="">{language === 'ar' ? 'اختر نوع الخدمة' : 'Select Service Type'}</option>
              {dropdowns?.service_types?.filter(s => s.is_active).map(service => (
                <option key={service.id} value={service.id}>
                  {language === 'ar' ? service.name_ar : service.name_en}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'طريقة الدفع *' : 'Payment Method *'}</label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
              className="w-full bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 text-white text-sm"
              required
            >
              <option value="">{language === 'ar' ? 'اختر طريقة الدفع' : 'Select Payment Method'}</option>
              {dropdowns?.payment_methods?.filter(p => p.is_active).map(method => (
                <option key={method.id} value={method.id}>
                  {language === 'ar' ? method.name_ar : method.name_en}
                </option>
              ))}
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'الوصف' : 'Description'}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 text-white text-sm resize-none"
            />
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-[#1a1a2e] rounded-xl p-6 border border-[#2a2a4e]">
        <h3 className="text-lg font-bold text-white mb-4">{language === 'ar' ? 'المبالغ' : 'Amounts'}</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'المبلغ قبل الضريبة' : 'Subtotal'}</label>
              <Input
                type="number"
                value={formData.subtotal}
                onChange={(e) => handleSubtotalChange(e.target.value)}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'نسبة الضريبة (%)' : 'Tax Rate (%)'}</label>
              <Input
                type="number"
                value={formData.tax_percentage}
                onChange={(e) => handleTaxChange(e.target.value)}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          </div>
          
          <div className="bg-[#0a0a0a] rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#808080]">{language === 'ar' ? 'المبلغ قبل الضريبة' : 'Subtotal'}</span>
              <span className="text-white"><DirhamPrice value={formData.subtotal} decimals={0} size="sm" /></span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#808080]">{language === 'ar' ? 'الضريبة' : 'Tax'} ({formData.tax_percentage}%)</span>
              <span className="text-white"><DirhamPrice value={formData.tax_amount} decimals={0} size="sm" /></span>
            </div>
            <div className="border-t border-[#2a2a4e] pt-2 flex justify-between">
              <span className="text-white font-bold">{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
              <span className="text-[#c9a227] font-bold text-lg"><DirhamPrice value={formData.total_amount} decimals={0} size="sm" /></span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status & Notes */}
      <div className="bg-[#1a1a2e] rounded-xl p-6 border border-[#2a2a4e]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'حالة الدفع' : 'Payment Status'}</label>
            <select
              value={formData.payment_status}
              onChange={(e) => setFormData(prev => ({ ...prev, payment_status: e.target.value }))}
              className="w-full bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 text-white text-sm"
            >
              {dropdowns?.payment_statuses?.filter(s => s.is_active).map(status => (
                <option key={status.id} value={status.id}>
                  {language === 'ar' ? status.name_ar : status.name_en}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-[#808080] mb-1">{language === 'ar' ? 'ملاحظات' : 'Notes'}</label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
            />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex gap-4">
        <Button
          type="button"
          onClick={() => { resetForm(); setActiveView('list'); }}
          className="flex-1 bg-[#2a2a4e] hover:bg-[#3a3a5e] text-white"
        >
          {language === 'ar' ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-[#c9a227] hover:bg-[#b8922a] text-black"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Receipt className="w-4 h-4 ml-2" />
              {language === 'ar' ? 'إنشاء الفاتورة' : 'Create Invoice'}
            </>
          )}
        </Button>
      </div>
    </form>
  );

  // ==================== Render Invoice Detail ====================
  const renderInvoiceDetail = () => {
    if (!selectedInvoice) return null;
    
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#1a1a2e] rounded-xl border border-[#2a2a4e] overflow-hidden">
          {/* Invoice Header */}
          <div className="bg-[#0f0f1a] p-6 border-b border-[#2a2a4e]">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-[#c9a227]">{selectedInvoice.invoice_number}</h2>
                <p className="text-sm text-[#808080] mt-1">
                  {language === 'ar' ? 'تاريخ الإنشاء:' : 'Created:'} {new Date(selectedInvoice.created_at).toLocaleDateString('ar-AE')}
                </p>
              </div>
              {getStatusBadge(selectedInvoice.payment_status)}
            </div>
          </div>
          
          {/* Customer Info */}
          <div className="p-6 border-b border-[#2a2a4e]">
            <h3 className="text-sm text-[#808080] mb-3">{language === 'ar' ? 'معلومات العميل' : 'Customer Information'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#606060]" />
                <span className="text-white">{selectedInvoice.customer_name}</span>
              </div>
              {selectedInvoice.customer_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#606060]" />
                  <span className="text-white">{selectedInvoice.customer_phone}</span>
                </div>
              )}
              {selectedInvoice.customer_email && (
                <div className="flex items-center gap-2 col-span-2">
                  <Mail className="w-4 h-4 text-[#606060]" />
                  <span className="text-white">{selectedInvoice.customer_email}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Service Details */}
          <div className="p-6 border-b border-[#2a2a4e]">
            <h3 className="text-sm text-[#808080] mb-3">{language === 'ar' ? 'تفاصيل الخدمة' : 'Service Details'}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-[#808080]">{language === 'ar' ? 'نوع الخدمة' : 'Service Type'}</span>
                <span className="text-white">{getDropdownLabel('service_types', selectedInvoice.service_type)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#808080]">{language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}</span>
                <span className="text-white">{getDropdownLabel('payment_methods', selectedInvoice.payment_method)}</span>
              </div>
              {selectedInvoice.description && (
                <div>
                  <span className="text-[#808080] block mb-1">{language === 'ar' ? 'الوصف' : 'Description'}</span>
                  <p className="text-white text-sm">{selectedInvoice.description}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Amounts */}
          <div className="p-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#808080]">{language === 'ar' ? 'المبلغ قبل الضريبة' : 'Subtotal'}</span>
                <span className="text-white"><DirhamPrice value={selectedInvoice.subtotal} decimals={0} size="sm" /></span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#808080]">{language === 'ar' ? 'الضريبة' : 'Tax'} ({selectedInvoice.tax_percentage}%)</span>
                <span className="text-white"><DirhamPrice value={selectedInvoice.tax_amount} decimals={0} size="sm" /></span>
              </div>
              <div className="border-t border-[#2a2a4e] pt-3 flex justify-between">
                <span className="text-white font-bold text-lg">{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                <span className="text-[#c9a227] font-bold text-xl"><DirhamPrice value={selectedInvoice.total_amount} decimals={0} size="sm" /></span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-4 mt-6">
          <Button
            onClick={() => { setSelectedInvoice(null); setActiveView('list'); }}
            className="flex-1 bg-[#2a2a4e] hover:bg-[#3a3a5e] text-white"
          >
            {language === 'ar' ? 'رجوع' : 'Back'}
          </Button>
          {selectedInvoice.payment_status !== 'paid' && (
            <Button
              onClick={() => updatePaymentStatus(selectedInvoice.id, 'paid')}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckCircle className="w-4 h-4 ml-2" />
              {language === 'ar' ? 'تحديد كمدفوع' : 'Mark as Paid'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Unified Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            {/* Right Side - Back & Logo */}
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (activeView !== 'list') {
                    setActiveView('list');
                    setSelectedInvoice(null);
                  } else if (fromFinance) {
                    // Navigate back to Finance > Payments
                    const isCustomer = platform === 'cloudgm';
                    navigate(isCustomer 
                      ? '/dashboard/finance/cloudgm/customers/payments'
                      : '/dashboard/finance/cloudgm/merchants/payments'
                    );
                  } else {
                    navigate(-1);
                  }
                }}
                className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
              >
                <ArrowRight className="h-5 w-5 text-[#d4af37]" />
              </motion.button>
              
              <div className="flex items-center gap-3">
                <motion.img 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  src={LOGO_TRIANGLE}
                  alt="ASB606"
                  className="w-10 h-10 object-contain drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                />
                <div>
                  <h1 className="text-lg font-bold bg-gradient-to-r from-[#d4af37] via-[#f3e5ab] to-[#c9a227] text-transparent bg-clip-text">
                    {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
                  </h1>
                  <p className="text-xs text-[#71717A] tracking-wider">ASB606 PLATFORMS GROUP</p>
                </div>
              </div>
            </div>

            {/* Left Side - Actions */}
            <div className="flex items-center gap-3">
              <LanguageToggle />
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
              >
                <Home className="h-5 w-5 text-[#A1A1AA]" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={logout}
                className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center hover:bg-red-500/20 transition-all border border-red-500/20"
              >
                <LogOut className="h-5 w-5 text-red-400" />
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => navigate('/dashboard')} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
              {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
            </button>
            <ChevronLeft className="h-4 w-4 text-[#71717A]" />
            <button onClick={() => navigate(`/control/${isCustomerPlatform ? 'cloudgm' : 'cloudgm-business'}`)} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
              CloudGM FZ LLC
            </button>
            <ChevronLeft className="h-4 w-4 text-[#71717A]" />
            <button onClick={() => navigate(`/control/${isCustomerPlatform ? 'cloudgm' : 'cloudgm-business'}`)} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
              {isCustomerPlatform ? (language === 'ar' ? 'منصة العملاء' : 'Customer Platform') : (language === 'ar' ? 'منصة التجار' : 'Merchant Platform')}
            </button>
            <ChevronLeft className="h-4 w-4 text-[#71717A]" />
            <button onClick={() => navigate(`/manage/${isCustomerPlatform ? 'cloudgm' : 'cloudgm-business'}`)} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
              {language === 'ar' ? 'الإشراف على المنصة' : 'Platform Supervision'}
            </button>
            <ChevronLeft className="h-4 w-4 text-[#71717A]" />
            <span className="text-[#d4af37]">
              {activeView === 'create' 
                ? (language === 'ar' ? 'إنشاء فاتورة' : 'Create Invoice')
                : activeView === 'detail'
                ? (language === 'ar' ? 'تفاصيل الفاتورة' : 'Invoice Details')
                : (language === 'ar' ? 'الفواتير' : 'Invoices')
              }
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Content */}
          {activeView === 'list' && renderInvoiceList()}
          {activeView === 'create' && renderCreateForm()}
          {activeView === 'detail' && renderInvoiceDetail()}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#808080]">
            {language === 'ar' 
              ? `هل أنت متأكد من حذف الفاتورة رقم ${invoiceToDelete?.invoice_number}؟`
              : `Are you sure you want to delete invoice ${invoiceToDelete?.invoice_number}?`
            }
          </p>
          <DialogFooter className="gap-2">
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              className="bg-[#2a2a4e] hover:bg-[#3a3a5e] text-white"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleDeleteInvoice}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {language === 'ar' ? 'حذف' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
